/**
 * Mermaid `stateDiagram-v2` → DiagramData importer (pure, unit-tested).
 *
 * Supports a practical subset:
 *   Header:      `stateDiagram-v2` / `stateDiagram`
 *   Transitions: `A --> B`, `A --> B : event [guard] / action`
 *   Initial/final: `[*] --> A` (initial), `A --> [*]` (final) — a shared
 *                  initial and a shared final pseudostate are synthesised.
 *   States:      `state "Long name" as id`, `id : description`
 *   Pseudostate: `state choice1 <<choice>>` / `<<fork>>` / `<<join>>`
 *   Composite:   `state Name { ... }` — the inner block is parsed flat (its
 *                states/transitions are emitted at top level) with a warning;
 *                full nesting awaits the ELK layout branch (B2).
 *
 * States carry `stateContent` so they round-trip through the same structured
 * node path as classes/tables. Transitions are ordinary labelled DiagramEdges.
 */

import type { DiagramData, DiagramNode, DiagramEdge, StateContent } from '../types/schema'

export interface MermaidStateImportResult {
  data: DiagramData
  warnings: string[]
}

interface ParsedState {
  id: string
  name: string
  kind: StateContent['kind']
  description?: string
}

const INITIAL_ID = '__initial__'
const FINAL_ID = '__final__'

function ensureState(id: string, states: Map<string, ParsedState>, name?: string): ParsedState {
  let s = states.get(id)
  if (!s) {
    s = { id, name: name ?? id, kind: 'simple' }
    states.set(id, s)
  } else if (name && s.name === s.id) {
    s.name = name
  }
  return s
}

/** Resolve a `[*]` operand to the shared initial/final node depending on side. */
function resolveEndpoint(token: string, side: 'source' | 'target', states: Map<string, ParsedState>): string {
  if (token === '[*]') {
    if (side === 'source') { ensureState(INITIAL_ID, states).kind = 'initial'; return INITIAL_ID }
    ensureState(FINAL_ID, states).kind = 'final'; return FINAL_ID
  }
  // Strip any alias quotes / generics noise.
  const id = token.replace(/"[^"]*"/g, '').trim().split(/\s+/)[0]
  ensureState(id, states)
  return id
}

export function parseMermaidStateDiagram(input: string): MermaidStateImportResult {
  const warnings: string[] = []
  const states = new Map<string, ParsedState>()
  const edges: DiagramEdge[] = []
  let sawHeader = false
  let compositeDepth = 0

  const lines = input.split('\n').map(l => l.trim()).filter(Boolean)
  for (let raw of lines) {
    if (raw.startsWith('%%')) continue
    if (/^stateDiagram(-v2)?\b/.test(raw)) { sawHeader = true; continue }
    if (/^(direction|title)\b/.test(raw)) continue

    // Composite block boundaries: parse contents flat.
    if (/\bstate\b[^:]*\{$/.test(raw)) {
      const nameMatch = /^state\s+(?:"([^"]+)"\s+as\s+)?([A-Za-z_][\w]*)/.exec(raw)
      if (nameMatch) { const s = ensureState(nameMatch[2], states, nameMatch[1]); s.kind = 'composite' }
      compositeDepth++
      if (compositeDepth === 1) warnings.push('Composite state flattened (nested layout pending ELK support).')
      continue
    }
    if (raw === '}') { if (compositeDepth > 0) compositeDepth--; continue }

    // `state X <<choice|fork|join>>`
    const pseudo = /^state\s+([A-Za-z_][\w]*)\s+<<(choice|fork|join)>>/.exec(raw)
    if (pseudo) { ensureState(pseudo[1], states).kind = pseudo[2] as StateContent['kind']; continue }

    // `state "Long name" as id`
    const alias = /^state\s+"([^"]+)"\s+as\s+([A-Za-z_][\w]*)/.exec(raw)
    if (alias) { ensureState(alias[2], states, alias[1]); continue }

    // `state id` bare declaration
    const bare = /^state\s+([A-Za-z_][\w]*)\s*$/.exec(raw)
    if (bare) { ensureState(bare[1], states); continue }

    // Transition: `A --> B` optionally `: label`
    const trans = /^(\[\*\]|[A-Za-z_][\w]*)\s*-->\s*(\[\*\]|[A-Za-z_][\w]*)\s*(?::\s*(.+))?$/.exec(raw)
    if (trans) {
      const source = resolveEndpoint(trans[1], 'source', states)
      const target = resolveEndpoint(trans[2], 'target', states)
      edges.push({
        id: `e-${source}-${target}-${edges.length}`,
        source,
        target,
        label: trans[3]?.trim(),
        edgeType: 'sequence',
        type: 'custom',
        data: { arrowEnd: 'arrow' },
      })
      continue
    }

    // `id : description`
    const desc = /^([A-Za-z_][\w]*)\s*:\s*(.+)$/.exec(raw)
    if (desc) { ensureState(desc[1], states).description = desc[2].trim(); continue }

    warnings.push(`Skipped unrecognised line: ${raw}`)
  }

  if (!sawHeader) warnings.push('No `stateDiagram` header found — parsed best-effort.')

  // Lay states on a simple grid; auto-layout (dagre TB) refines afterwards.
  const nodes: DiagramNode[] = []
  let idx = 0
  const COLS = 3
  for (const s of states.values()) {
    nodes.push({
      id: s.id,
      label: s.name,
      nodeType: 'node',
      position: { x: (idx % COLS) * 220, y: Math.floor(idx / COLS) * 160 },
      stateContent: { stateName: s.name, kind: s.kind, description: s.description },
    })
    idx++
  }

  const data: DiagramData = {
    diagramType: 'state',
    title: 'State Diagram',
    nodes,
    edges,
    layout: { algorithm: 'dagre', direction: 'TB' },
  }
  return { data, warnings }
}
